'use client';

import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { sendGTMEvent } from '@next/third-parties/google';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

export default function Page() {
  return (
    <div className="mt-10 p-6 rounded-lg">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-white">
          Sign in to your account
        </h2>
        <p className="mt-2 text-center text-sm text-white">
          Or{' '}
          <a
            href="https://swingular.com"
            className="font-medium hover:text-orange-500 text-orange-300"
          >
            start your 14-day free trial
          </a>
        </p>
      </div>
      <Suspense fallback={<div>Loading...</div>}>
        <LoginForm />
      </Suspense>
    </div>
  );
}

function LoginForm() {
  const form = useForm({
    defaultValues: {
      username: '',
      password: '',
      remember: false,
    },
  });

  // Track login page view when component mounts
  useEffect(() => {
    sendGTMEvent({
      event: 'login_page_view',
      timestamp: new Date().toISOString(),
      referrer: document.referrer || 'direct'
    });
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || '/home';

  const onSubmit = async (data) => {
    setIsLoading(true);
    setLoginError(null);

    // Track login attempt
    sendGTMEvent({
      event: 'login_attempt',
      method: 'credentials',
      timestamp: new Date().toISOString()
    });

    console.log('Form submission data:', {
      hasUsername: !!data.username,
      hasPassword: !!data.password,
      username: data.username,
      passwordLength: data.password ? data.password.length : 0,
    });

    const result = await signIn('credentials', {
      username: data.username,
      password: data.password,
      redirect: false,
      callbackUrl,
    });

    setIsLoading(false);

    if (result?.error === 'CredentialsSignin') {
      setLoginError('Invalid email or password');
      
      // Track login failure - invalid credentials
      sendGTMEvent({
        event: 'login_failure',
        method: 'credentials',
        error_type: 'invalid_credentials',
        timestamp: new Date().toISOString()
      });
    } else if (result?.error) {
      setLoginError('An error occurred during login');
      
      // Track login failure - system error
      sendGTMEvent({
        event: 'login_failure',
        method: 'credentials',
        error_type: 'system_error',
        error_message: result.error,
        timestamp: new Date().toISOString()
      });
    } else {
      // Track login success
      sendGTMEvent({
        event: 'login_success',
        method: 'credentials',
        redirect_url: callbackUrl,
        timestamp: new Date().toISOString()
      });
      
      router.push(callbackUrl);
    }
  };

  // WebAuthn login is not yet fully implemented
  const handleWebAuthnLogin = async () => {
    try {
      // Track WebAuthn login attempt
      sendGTMEvent({
        event: 'login_attempt',
        method: 'webauthn',
        timestamp: new Date().toISOString()
      });
      
      // TODO: Implement WebAuthn login
      // Temporarily disabled to prevent hydration errors
      // await loginWithWebAuthn();
      setLoginError('WebAuthn login is not yet implemented.');
      
      // Track WebAuthn login failure - not implemented
      sendGTMEvent({
        event: 'login_failure',
        method: 'webauthn',
        error_type: 'not_implemented',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setLoginError('WebAuthn login failed. Please try again.');
      
      // Track WebAuthn login error
      sendGTMEvent({
        event: 'login_failure',
        method: 'webauthn',
        error_type: 'system_error',
        error_message: error.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  };

  return (
    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-black opacity-80 py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
        {loginError && (
          <div className="mb-4 text-center text-sm text-white border-2 border-red-600 rounded-lg p-2">
            {loginError}
          </div>
        )}
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email/Username</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between">
              <FormField
                control={form.control}
                name="remember"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>Remember me</FormLabel>
                  </FormItem>
                )}
              />

              <div className="text-sm">
                <a
                  href="#"
                  className="font-medium hover:text-orange-500 text-orange-300"
                >
                  Forgot your password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-blue-600 py-2 px-3 text-sm font-semibold text-white shadow-xs hover:bg-blue-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <span className="loading loading-spinner loading-sm" />
                  <span className="ml-2">Signing In</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </Form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with
              </span>
            </div>
          </div>

          <div className="mt-6">
            <button
              disabled={true}
              onClick={handleWebAuthnLogin}
              className="w-full flex justify-center rounded-md bg-gray-600 py-2 px-3 text-sm font-semibold text-white shadow-xs hover:bg-gray-500"
            >
              Login with Biometrics
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
